/**
 * SkiSupply — deal countdown.
 *
 * Counts down to one fixed moment supplied by the server, so the clock reads
 * the same for everyone and a refresh does not buy more time. At zero the bar
 * removes itself rather than sitting at zero or looping.
 *
 * The visible digits are aria-hidden and the real end time is exposed once as
 * text: announcing a changing number every second is unusable.
 */

(() => {
  'use strict';

  const root = document.documentElement;
  const pad = (n) => String(n).padStart(2, '0');
  const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

  function split(totalSeconds) {
    return {
      days: Math.floor(totalSeconds / 86400),
      hours: Math.floor((totalSeconds % 86400) / 3600),
      minutes: Math.floor((totalSeconds % 3600) / 60),
      seconds: totalSeconds % 60,
    };
  }

  /** "0 days 0 hours 28 minutes and 47 seconds" */
  function format(totalSeconds) {
    const { days, hours, minutes, seconds } = split(totalSeconds);

    // Minutes and seconds stay two digits so the row keeps a constant width and
    // the text after them does not shuffle sideways every tick.
    return `${plural(days, 'day')} ${plural(hours, 'hour')} ` +
           `${pad(minutes)} minutes and ${pad(seconds)} seconds`;
  }

  /**
   * Per-visitor demo mode: the same fixed window for everyone, beginning at
   * their first visit. The end time is kept in localStorage so moving between
   * pages continues the same clock instead of restarting it, and once it runs
   * out a fresh window begins so the bar never empties during a demo.
   *
   * Intended for a welcome offer that genuinely expires for that visitor, so
   * the clock states something true about their session.
   */
  const VISITOR_KEY = 'ss:deal-window:v2';

  function visitorEndsAt(windowSeconds) {
    const now = Math.floor(Date.now() / 1000);
    try {
      const stored = Number(localStorage.getItem(VISITOR_KEY));
      // A stored value further out than the window itself is stale or tampered
      // with; start over rather than trusting it.
      if (Number.isFinite(stored) && stored > now && stored <= now + windowSeconds) {
        return stored;
      }
    } catch {
      // Storage unavailable: fall through and run the window for this page only.
    }

    const fresh = now + windowSeconds;
    try {
      localStorage.setItem(VISITOR_KEY, String(fresh));
    } catch {
      /* nothing to persist to; the countdown still works for this page view */
    }
    return fresh;
  }

  document.querySelectorAll('[data-ss-countdown]').forEach((bar) => {
    const output = bar.querySelector('[data-countdown-output]');
    if (!output) return;

    const perVisitor = bar.dataset.countdownMode === 'per_visitor';
    const windowSeconds = Number(bar.dataset.visitorWindow) || 0;

    let endsAt = perVisitor && windowSeconds
      ? visitorEndsAt(windowSeconds)
      : Number(bar.dataset.endsAt);

    if (!endsAt) return;

    // Two presentations of the same clock, chosen in the theme editor. The
    // segment version updates each digit block in place rather than rewriting
    // the whole node, so the labels above them are never touched.
    const segments = output.dataset.countdownFormat === 'segments'
      ? {
          days: output.querySelector('[data-seg="days"]'),
          hours: output.querySelector('[data-seg="hours"]'),
          minutes: output.querySelector('[data-seg="minutes"]'),
          seconds: output.querySelector('[data-seg="seconds"]'),
        }
      : null;

    // The bar is sticky and so is the header, so its measured height is
    // published for the stylesheet to park the header directly beneath.
    // Measured, not hardcoded: the height moves with font size, viewport, and
    // whether the row wraps.
    const publishHeight = () => {
      root.style.setProperty(
        '--ss-deal-bar-height',
        `${Math.round(bar.getBoundingClientRect().height)}px`
      );
    };

    const tick = () => {
      const remaining = Math.floor(endsAt - Date.now() / 1000);

      if (remaining <= 0) {
        // A demo window loops rather than ending, so the bar stays put.
        if (perVisitor && windowSeconds) {
          try {
            localStorage.removeItem(VISITOR_KEY);
          } catch {
            /* nothing stored to clear */
          }
          endsAt = visitorEndsAt(windowSeconds);
          return;
        }

        clearInterval(timer);
        bar.remove();
        // Collapse the offset with the bar, or the header parks below nothing.
        root.style.setProperty('--ss-deal-bar-height', '0px');
        return;
      }

      if (segments) {
        const parts = split(remaining);
        for (const key of Object.keys(segments)) {
          if (segments[key]) segments[key].textContent = pad(parts[key]);
        }
      } else {
        output.textContent = format(remaining);
      }
    };

    const timer = setInterval(tick, 1000);
    tick();
    publishHeight();

    if ('ResizeObserver' in window) {
      new ResizeObserver(publishHeight).observe(bar);
    } else {
      addEventListener('resize', publishHeight, { passive: true });
    }

    // A backgrounded tab throttles timers; resync on return rather than
    // letting the display drift behind the real deadline.
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) tick();
    });
  });
})();

/**
 * Keep the header's solid/transparent state correct while it sits below the
 * sticky deal bar.
 *
 * Horizon decides the header is "stuck" with an IntersectionObserver at
 * threshold 1: the header parks at top:-1px, so one pixel leaves the viewport,
 * the ratio drops below 1, and it switches to its solid state. Parked underneath
 * the deal bar the header is never clipped, the ratio stays at 1, and it stays
 * transparent forever — which reads as the header having lost its white
 * background on scroll.
 *
 * Horizon's own scroll handler returns early for sticky="always" without
 * touching stickyState, so nothing fights this.
 */
(() => {
  'use strict';

  const header = document.querySelector('#header-component[sticky="always"]');
  const bar = document.querySelector('[data-ss-countdown]');
  if (!header || !bar) return;

  let frame = null;

  const sync = () => {
    frame = null;

    // Purely geometric: has the header reached its resting place under the bar?
    // Deliberately not based on scroll position — Horizon scrolls an inner
    // .page-wrapper rather than the window on some layouts, so window.scrollY
    // sits at 0 forever and any test built on it silently never fires.
    const perch = bar.getBoundingClientRect().height;
    const parked = header.getBoundingClientRect().top <= perch + 1;

    const next = parked ? 'active' : 'inactive';
    if (header.dataset.stickyState !== next) header.dataset.stickyState = next;
  };

  const schedule = () => {
    if (frame === null) frame = requestAnimationFrame(sync);
  };

  // Capture phase catches scroll from whichever element is actually scrolling,
  // window or inner container, without having to know which.
  document.addEventListener('scroll', schedule, { capture: true, passive: true });
  addEventListener('resize', schedule, { passive: true });
  sync();
})();
